<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link" class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="flex: 1 1 180px; max-width: 260px;">
              <label class="filter-label">Módulo</label>
              <b-form-select size="sm" v-model="filter.module_id" :options="moduleOptions"></b-form-select>
            </div>
            <div class="list-view-filter" style="flex: 1 1 150px; max-width: 200px;">
              <label class="filter-label">Canal</label>
              <b-form-select size="sm" v-model="filter.channel" :options="arr.select.channels"></b-form-select>
            </div>
            <div class="list-view-filter" style="flex: 1 1 180px; max-width: 230px;">
              <label class="filter-label">Implementación</label>
              <b-form-select size="sm" v-model="filter.self_managed" :options="arr.select.self_managed"></b-form-select>
            </div>
            <div class="list-view-filter" style="flex: 1 1 150px; max-width: 190px;">
              <label class="filter-label">Soporte</label>
              <b-form-select size="sm" v-model="filter.deprecated" :options="arr.select.deprecated"></b-form-select>
            </div>
          </div>
        </b-collapse>
      </b-card>

      <b-card>
            <b-table class="app-table notifications-event-types-table"
                  responsive
                  :stacked="isMobileViewport ? 'md' : false"
                  sticky-header="60vh"
                  :hover="true"
                  :items="filteredItems"
                  :fields="table.fields"
                  :tbody-tr-class="rowClass"
                  @row-clicked="onRowClicked"
                  v-if="filteredItems.length">

              <template v-slot:table-colgroup="scope">
                <col v-for="field in scope.fields" :key="field.key" :style="{ width: field.width }">
              </template>

              <template v-slot:cell(label)="data">
                <strong :class="{ 'text-danger': data.item.deprecated }">{{ data.item.label }}</strong>
                <b-badge variant="danger" class="ml-1" v-if="data.item.deprecated" v-b-tooltip.hover title="Este proceso está discontinuado, ya no tiene soporte">
                  <AppIcon name="warning" class="mr-1" />Sin soporte
                </b-badge>
                <b-badge variant="secondary" class="ml-1" v-if="data.item.self_managed" v-b-tooltip.hover title="Este evento ya envía notificaciones por su propia pantalla de configuración, no se gestiona desde acá">
                  <AppIcon name="settings" class="mr-1" />Implementación propia
                </b-badge>
                <b-badge variant="warning" class="ml-1" v-if="data.item.notifies_external" v-b-tooltip.hover title="También notifica a personas externas (clientes/proveedores), fuera del staff interno">
                  <AppIcon name="public" class="mr-1" />Externos
                </b-badge>
                <b-badge variant="info" class="ml-1" v-if="data.item.notifies_contextual" v-b-tooltip.hover title="También notifica a una persona puntual del proceso (quien lo generó, el responsable asignado, etc.), resuelta dinámicamente por el código, no configurable acá">
                  <AppIcon name="person_check" class="mr-1" />Personas del proceso
                </b-badge>
                <div class="text-muted">{{ data.item.reference }}</div>
                <div class="text-muted" v-if="data.item.description">{{ data.item.description }}</div>
              </template>

              <template v-slot:cell(module)="data">
                <b-badge variant="info" v-if="data.item.module">
                  {{ moduleLabel(data.item.module) }}
                </b-badge>
                <span class="text-muted" v-else>Sistema</span>
              </template>

              <template v-slot:cell(channels)="data">
                <b-badge :variant="channelVariant(channel)" class="mr-1" v-for="channel in parseChannels(data.item.channels)" :key="channel">
                  {{ channelLabel(channel) }}
                </b-badge>
              </template>

              <template v-slot:cell(recipients)="data">
                <template v-if="data.item.self_managed">
                  <span class="text-muted">Se configura en su propia pantalla</span>
                </template>
                <template v-else-if="!countRecipients(data.item.staff_notification) && !countRecipients(data.item.roles_notification)">
                  <span class="text-muted">Sin configurar</span>
                </template>
                <div class="notifications-recipients-details" v-else>
                  <div v-if="recipientNames(data.item, 'roles').length">
                    <AppIcon name="group" class="notifications-recipients-details__icon" />
                    <span>{{ recipientNames(data.item, 'roles').join(', ') }}</span>
                  </div>
                  <div v-if="recipientNames(data.item, 'staff').length">
                    <AppIcon name="person" class="notifications-recipients-details__icon" />
                    <span>{{ recipientNames(data.item, 'staff').join(', ') }}</span>
                  </div>
                </div>
              </template>

              <template v-slot:cell(actions)="data">
                <div>
                  <b-dropdown size="sm" variant="link" boundary="window"
                              toggle-class="app-table-action-toggle notifications-event-types-action-toggle"
                              right
                              no-caret>
                    <template v-slot:button-content>
                      <AppIcon name="more_vert" :label="isMobileViewport ? 'Opciones' : 'Acciones'" />
                      <span v-if="isMobileViewport" class="notifications-event-types-options-label">Opciones</span>
                    </template>

                    <b-dropdown-item v-if="!data.item.self_managed" @click="editRecipients(data.item)">
                      <AppIcon name="group" class="mr-2" />Destinatarios
                    </b-dropdown-item>
                    <b-dropdown-item v-if="!data.item.self_managed" @click="sendTest(data.item)">
                      <AppIcon name="send" class="mr-2" />Enviar prueba
                    </b-dropdown-item>
                    <b-dropdown-item @click="viewLog(data.item)">
                      <AppIcon name="description" class="mr-2" />Ver notificaciones
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </template>

            </b-table>
            <EmptyState v-else icon="notifications" title="No se encontraron tipos de evento"
                        description="Ajustá los filtros para ampliar la búsqueda." />
            <div class="app-table-footer">
              <span class="list-view-toolbar__count">{{ filteredItems.length }} tipos de evento</span>
            </div>
      </b-card>

      <b-sidebar id="sidebar-notification-event-type-detail" v-model="detailDrawer" right shadow backdrop
                 title="Detalle del tipo de evento" width="420px" @hidden="hideSub">
        <div class="list-view-drawer" v-if="itemSelected">
          <div class="list-view-drawer__header">
            <div class="list-view-drawer__title">
              <span class="list-view-drawer__code">{{ itemSelected.reference }}</span>
              <div>{{ itemSelected.label }}</div>
              <div class="list-view-drawer__meta">{{ itemSelected.module ? moduleLabel(itemSelected.module) : 'Sistema' }}</div>
            </div>
            <div class="list-view-drawer__nav">
              <b-button size="sm" variant="outline-secondary" :disabled="!canGoPrev"
                        v-b-tooltip.hover title="Tipo de evento anterior" @click="goPrev()">
                <AppIcon name="chevron-up" />
              </b-button>
              <b-button size="sm" variant="outline-secondary" :disabled="!canGoNext"
                        v-b-tooltip.hover title="Tipo de evento siguiente" @click="goNext()">
                <AppIcon name="chevron-down" />
              </b-button>
            </div>
          </div>
          <div class="list-view-drawer__stats">
            <div class="list-view-drawer__stat">
              <span class="list-view-drawer__stat-label">Módulo</span>
              <span class="list-view-drawer__stat-value">{{ itemSelected.module ? moduleLabel(itemSelected.module) : 'Sistema' }}</span>
            </div>
            <div class="list-view-drawer__stat" :class="{ 'list-view-drawer__stat--warning': itemSelected.deprecated }">
              <span class="list-view-drawer__stat-label">Soporte</span>
              <span class="list-view-drawer__stat-value"><AppIcon :name="itemSelected.deprecated ? 'warning' : 'verified'" class="mr-1" />{{ itemSelected.deprecated ? 'Sin soporte' : 'Con soporte' }}</span>
            </div>
          </div>
          <div v-if="itemSelected.description">
            <div class="list-view-drawer__section-title"><AppIcon name="description" /> Descripción</div>
            <div class="notifications-event-types-drawer-text">{{ itemSelected.description }}</div>
          </div>
          <div>
            <div class="list-view-drawer__section-title"><AppIcon name="notifications" /> Canales</div>
            <b-badge :variant="channelVariant(channel)" class="mr-1" v-for="channel in parseChannels(itemSelected.channels)" :key="channel">
              <AppIcon :name="channelIcon(channel)" class="mr-1" />{{ channelLabel(channel) }}
            </b-badge>
          </div>
          <div>
            <div class="list-view-drawer__section-title"><AppIcon name="group" /> Destinatarios</div>
            <div class="notifications-event-types-drawer-text" v-if="itemSelected.self_managed">Se configura en su propia pantalla.</div>
            <div class="notifications-event-types-drawer-text" v-else-if="!countRecipients(itemSelected.roles_notification) && !countRecipients(itemSelected.staff_notification)">Sin destinatarios configurados.</div>
            <div class="notifications-event-types-recipient-list" v-else>
              <div v-if="recipientNames(itemSelected, 'roles').length">
                <span class="notifications-event-types-recipient-list__label"><AppIcon name="group" /> Roles</span>
                <b-badge variant="primary" v-for="name in recipientNames(itemSelected, 'roles')" :key="'role_'+name" class="mr-1 mb-1">{{ name }}</b-badge>
              </div>
              <div v-if="recipientNames(itemSelected, 'staff').length">
                <span class="notifications-event-types-recipient-list__label"><AppIcon name="person" /> Staff</span>
                <b-badge variant="success" v-for="name in recipientNames(itemSelected, 'staff')" :key="'staff_'+name" class="mr-1 mb-1">{{ name }}</b-badge>
              </div>
            </div>
          </div>
          <div>
            <div class="list-view-drawer__section-title"><AppIcon name="settings" /> Configuración</div>
            <div class="notifications-event-types-config-list">
              <span><AppIcon :name="itemSelected.self_managed ? 'settings' : 'tune'" />{{ itemSelected.self_managed ? 'Implementación propia' : 'Destinatarios configurables acá' }}</span>
              <span v-if="itemSelected.notifies_external"><AppIcon name="public" />También notifica a externos</span>
              <span v-if="itemSelected.notifies_contextual"><AppIcon name="person_check" />También notifica a personas del proceso</span>
            </div>
          </div>
          <div class="list-view-drawer__actions">
            <b-button size="sm" variant="outline-secondary" v-if="!itemSelected.self_managed" @click="editRecipients(itemSelected)">
              <AppIcon name="group" class="mr-1" /> Configurar destinatarios
            </b-button>
            <b-button size="sm" variant="outline-secondary" v-if="!itemSelected.self_managed" @click="sendTest(itemSelected)">
              <AppIcon name="send" class="mr-1" /> Enviar prueba
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="viewLog(itemSelected)">
              <AppIcon name="description" class="mr-1" /> Ver notificaciones
            </b-button>
          </div>
        </div>
      </b-sidebar>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)"><AppIcon name="back" /> Volver</b-button>
        </div>
      </b-card>

      <b-modal v-model="modal.form.active"
               size="lg"
               :key="modal.form._id"
               :id="modal.form._id">
        <div slot="modal-header" class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="group" /></span>
          <h5 class="app-modal-header__title">{{ modal.form.title }}</h5>
        </div>

        <EventTypeRecipients @select-recipients="getRecipientsData($event)"
                              :roles_notification="crud.form.roles_notification"
                              :staff_notification="crud.form.staff_notification" />

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.form.active=false">Cancelar</b-button>
          <b-button variant="dark" @click="save()"><AppIcon name="save" class="mr-1" />Guardar</b-button>
        </div>
      </b-modal>

    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import Storage from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import EventTypeRecipients from './inc/eventTypeRecipients'

  export default {
    components: {
      EventTypeRecipients,
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.NOTIFICACIONES,
          profile_id: Profiles.PERSONAL,
          view_reference: 'eventTypes',
          elements: {}
        },
        table: {
          items: [],
          fields: [],
        },
        filtersOpen: false,
        isMobileViewport: false,
        itemSelected: null,
        detailDrawer: false,
        filter: {
          module_id: '',
          channel: '',
          self_managed: '',
          deprecated: '',
        },
        arr: {
          select: {
            channels: [
              { value: '', text: 'TODOS' },
              { value: 'internal', text: 'Interna' },
              { value: 'email', text: 'Email' },
              { value: 'sms', text: 'SMS' },
              { value: 'whatsapp', text: 'WhatsApp' },
              { value: 'push', text: 'Push' },
            ],
            self_managed: [
              { value: '', text: 'TODAS' },
              { value: '0', text: 'Configurada acá' },
              { value: '1', text: 'Implementación propia' },
            ],
            deprecated: [
              { value: '', text: 'TODAS' },
              { value: '0', text: 'Con soporte' },
              { value: '1', text: 'Sin soporte' },
            ],
          }
        },
        modal: {
          form: {
            active: false,
            _id: '',
            title: '',
          }
        },
        crud: {
          form: {
            id: null,
            roles_notification: [],
            staff_notification: [],
          }
        },
        // Nombres de roles/staff, para resolver el tooltip de "Destinatarios"
        // sin tener que abrir el modal de edición.
        names: {
          roles: {},
          staff: {},
        },
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */

      this.restoreFilterStorage()
    },
    mounted() {
      this.filtersOpen = Storage.getValue('filters_open_notifications_event_types') == 'true'
      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)
      this.loadFieldTable()
      this.search()
      this.loadNames()

      this._start = true
    },
    beforeDestroy() {
      if(this.mobileMql) this.mobileMql.removeEventListener('change', this.updateMobileViewport)
    },
    activated() {
      if (this._start) {
        this._start = false
        return
      }

      this.search()
      this.loadNames()
    },
    watch: {
      filter: {
        handler() {
          this.saveFilterStorage()
        },
        deep: true
      },
      filtersOpen(value) {
        Storage.setValue('filters_open_notifications_event_types', value ? 'true' : 'false')
      },
      isMobileViewport() {
        this.loadFieldTable()
      }
    },
    computed: {
      filteredItems() {
        return this.table.items.filter(item => {
          if (this.filter.module_id && item.module_id != this.filter.module_id) return false
          if (this.filter.channel && !this.parseChannels(item.channels).includes(this.filter.channel)) return false
          if (this.filter.self_managed !== '' && Boolean(item.self_managed) != Boolean(Number(this.filter.self_managed))) return false
          if (this.filter.deprecated !== '' && Boolean(item.deprecated) != Boolean(Number(this.filter.deprecated))) return false
          return true
        })
      },
      moduleOptions() {
        var seen = {}
        var options = [{ value: '', text: 'TODOS' }]

        this.table.items.forEach(item => {
          if (item.module && !seen[item.module_id]) {
            seen[item.module_id] = true
            options.push({ value: item.module_id, text: this.moduleLabel(item.module) })
          }
        });

        return options
      },
      currentIndex() {
        return this.itemSelected ? this.filteredItems.indexOf(this.itemSelected) : -1
      },
      canGoPrev() {
        return this.currentIndex > 0
      },
      canGoNext() {
        return this.currentIndex >= 0 && this.currentIndex < this.filteredItems.length - 1
      }
    },
    methods: {
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      loadFieldTable() {
        this.table.fields = [
          {key: 'label', label: 'Tipo de evento', class: "align-middle text-left", width: "340px"},
          {key: 'module', label: 'Módulo', class: "align-middle text-left", width: "160px"},
          {key: 'channels', label: 'Canal', class: "align-middle text-left", width: "210px"},
          {key: 'recipients', label: 'Destinatarios', class: "align-middle text-left", width: "210px"},
          {key: 'actions', label: this.isMobileViewport ? 'Opciones' : '', class: "align-middle text-left", width: this.isMobileViewport ? '' : "50px"},
        ]
      },
      onRowClicked(item, index, event) {
        if(!item) return
        if(event && event.target && (event.target.closest('.dropdown') || ['BUTTON', 'A', 'SVG', 'PATH'].includes(event.target.tagName))) return
        this.itemSelected = item
        this.detailDrawer = true
      },
      hideSub() {
        this.itemSelected = null
        this.detailDrawer = false
      },
      goPrev() {
        if(this.canGoPrev) this.itemSelected = this.filteredItems[this.currentIndex - 1]
      },
      goNext() {
        if(this.canGoNext) this.itemSelected = this.filteredItems[this.currentIndex + 1]
      },
      search() {
        let loader = this.$loading.show()

        serviceAPI.listEventTypes()
          .then((response) => {
            loader.hide()
            this.table.items = response.data
          })
          .catch(error => {
            loader.hide()
            this.$awn.alert(Error.showError(error))
          })
      },
      countRecipients(value) {
        if (!value) return 0
        return JSON.parse(value).length
      },
      loadNames() {
        serviceAPI.obtenerRoles().then(response => {
          response.data.forEach(role => {
            this.$set(this.names.roles, role.id, role.name)
          })
        })

        serviceAPI.obtenerStaff().then(response => {
          response.data.forEach(staff => {
            this.$set(this.names.staff, staff.id, staff.name)
          })
        })
      },
      recipientsTooltip(item) {
        var parts = []

        var roleIds = item.roles_notification ? JSON.parse(item.roles_notification) : []
        if (roleIds.length) {
          var roleNames = roleIds.map(id => this.names.roles[id] || ('#' + id))
          parts.push('<strong>Roles:</strong> ' + roleNames.join(', '))
        }

        var staffIds = item.staff_notification ? JSON.parse(item.staff_notification) : []
        if (staffIds.length) {
          var staffNames = staffIds.map(id => this.names.staff[id] || ('#' + id))
          parts.push('<strong>Staff:</strong> ' + staffNames.join(', '))
        }

        return parts.join('<br>')
      },
      moduleLabel(module) {
        var label = module.title || module.name
        return label.substring(label.indexOf('.') + 1)
      },
      parseChannels(value) {
        if (!value) return ['internal']
        try {
          return JSON.parse(value)
        } catch (e) {
          return ['internal']
        }
      },
      channelLabel(channel) {
        var labels = { internal: 'Interna', email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp', push: 'Push' }
        return labels[channel] || channel
      },
      channelVariant(channel) {
        var variants = { internal: 'info', email: 'primary', sms: 'secondary', whatsapp: 'success', push: 'dark' }
        return variants[channel] || 'secondary'
      },
      channelIcon(channel) {
        var icons = { internal: 'notifications', email: 'mail', sms: 'sms', whatsapp: 'forum', push: 'notifications' }
        return icons[channel] || 'notifications'
      },
      recipientsSummary(item) {
        if(item.self_managed) return 'Se configura en su propia pantalla'
        var roles = this.countRecipients(item.roles_notification)
        var staff = this.countRecipients(item.staff_notification)
        if(!roles && !staff) return 'Sin destinatarios configurados'
        return [roles ? roles + ' rol/es' : '', staff ? staff + ' staff' : ''].filter(Boolean).join(' · ')
      },
      recipientNames(item, type) {
        var source = type == 'roles' ? item.roles_notification : item.staff_notification
        var names = type == 'roles' ? this.names.roles : this.names.staff
        if(!source) return []
        try {
          return JSON.parse(source).map(id => names[id] || ('#' + id))
        } catch (e) {
          return []
        }
      },
      rowClass(item, type) {
        if(type !== 'row' || !item) return ''
        if(this.detailDrawer && this.itemSelected && item.id === this.itemSelected.id) return 'app-table-row-active'
        if (!item) return ''
        if (item.deprecated) return 'notifications-event-types-row-deprecated'
        return ''
      },
      saveFilterStorage() {
        Storage.setValue('filter_view_notifications_event_types', JSON.stringify(this.filter))
      },
      restoreFilterStorage() {
        if (Storage.getValue('filter_view_notifications_event_types')) {
          Object.assign(this.filter, JSON.parse(Storage.getValue('filter_view_notifications_event_types')))
        }
      },
      editRecipients(item) {
        this.crud.form.id = item.id
        this.crud.form.roles_notification = item.roles_notification ? JSON.parse(item.roles_notification) : []
        this.crud.form.staff_notification = item.staff_notification ? JSON.parse(item.staff_notification) : []

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Destinatarios: " + item.label
        this.modal.form.active = true
      },
      getRecipientsData(data) {
        var roles = []
        if (data.roles_notification) {
          data.roles_notification.forEach(element => {
            roles.push(element.code)
          });
        }
        this.crud.form.roles_notification = roles

        var staff = []
        if (data.staff_notification) {
          data.staff_notification.forEach(element => {
            staff.push(element.code)
          });
        }
        this.crud.form.staff_notification = staff
      },
      save() {
        let loader = this.$loading.show()

        serviceAPI.updateEventTypeRecipients(this.crud.form.id, {
          roles_notification: this.crud.form.roles_notification,
          staff_notification: this.crud.form.staff_notification,
        })
        .then(() => {
          loader.hide()
          this.modal.form.active = false
          this.$awn.success("Destinatarios actualizados")
          this.search()
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      viewLog(item) {
        // Los procesos "implementación propia" no siempre loguean con el mismo
        // reference que usamos acá para catalogarlos (muchos loguean con
        // reference vacío) — para esos solo filtramos por módulo, para no
        // mostrar "0 resultados" con un filtro que nunca va a matchear.
        var query = { module_id: item.module_id }
        if (!item.self_managed) {
          query.reference = item.reference
        }
        this.$router.push({ name: 'NotificationsStaffLog', query: query })
      },
      sendTest(item) {
        serviceAPI.sendEventTypeTest(item.id)
          .then((response) => {
            var result = response.data
            var successCount = result.filter(r => r.status).length

            if (successCount) {
              this.$awn.success("Notificación de prueba enviada a " + successCount + " destinatario(s)")
            } else {
              var error = (result[0] && result[0].error) ? result[0].error : "No se pudo enviar la notificación de prueba"
              this.$awn.alert(error)
            }
          })
          .catch(error => {
            this.$awn.alert(Error.showError(error))
          })
      },
    }
  }
</script>
<style scoped>
  .notifications-recipients-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: var(--app-color-text-secondary);
    font-size: var(--app-text-sm);
  }
  .notifications-recipients-details > div {
    display: flex;
    align-items: flex-start;
    gap: 5px;
  }
  .notifications-recipients-details__icon {
    margin-top: 2px;
    color: var(--app-color-text-muted);
  }
</style>
<style>
  .notifications-event-types-row-deprecated {
    box-shadow: inset 3px 0 0 0 var(--app-color-danger);
    background-color: var(--app-color-danger-soft);
  }
  .notifications-event-types-drawer-text {
    color: var(--app-color-text);
    font-size: var(--app-text-sm);
    line-height: 1.45;
    white-space: pre-line;
    word-break: break-word;
  }
  .notifications-event-types-recipient-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .notifications-event-types-recipient-list__label,
  .notifications-event-types-config-list span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: var(--app-color-text-secondary);
    font-size: var(--app-text-xs);
    font-weight: var(--app-font-semibold);
  }
  .notifications-event-types-config-list {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }
  .notifications-event-types-options-label { display: none; }
  @media (max-width: 767.98px) {
    .notifications-event-types-table td {
      text-align: left !important;
      white-space: normal !important;
    }
    .notifications-event-types-table td > * {
      max-width: 100%;
      white-space: normal;
      word-break: break-word;
    }
    .notifications-event-types-table .badge {
      white-space: normal;
      text-align: left;
    }
    .notifications-event-types-action-toggle {
      display: inline-flex !important;
      align-items: center;
      gap: 4px;
      width: auto !important;
      min-height: 26px;
      padding: 2px 5px !important;
      font-size: var(--app-text-xs) !important;
    }
    .notifications-event-types-options-label { display: inline; }
  }
</style>
